import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2, XCircle, Loader2, RefreshCw, KeyRound, PlugZap, Package, CreditCard, Users } from 'lucide-react';
import { ConciliacaoTip } from '../components/ConciliacaoTip';
import { DivergenciasTip } from '../components/DivergenciasTip';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TIP_URL_PADRAO = 'https://mvno.tipbrasil.com.br/api/public';

function SyncButtons({ fornecedor, disabled, onDone }) {
  const [rodando, setRodando] = useState(null);
  const rodar = async (tipo, rotulo) => {
    if (tipo === 'clientes' && !window.confirm(`Importar clientes e linhas da ${fornecedor === 'tip' ? 'Tip Brasil' : 'Tá Telecom'}? Pode levar vários minutos.`)) return;
    setRodando(tipo);
    try {
      const { data } = await axios.post(`${API_URL}/api/operadora/sincronizar-${tipo}?fornecedor=${fornecedor}`, {}, { withCredentials: true });
      toast.success(data.message || `${rotulo} sincronizados`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Erro ao sincronizar ${rotulo.toLowerCase()}`);
    }
    setRodando(null);
  };
  const itens = [
    { tipo: 'planos', rotulo: 'Planos', Icon: Package },
    { tipo: 'estoque', rotulo: 'Chips (estoque)', Icon: CreditCard },
    { tipo: 'clientes', rotulo: 'Clientes e linhas', Icon: Users },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {itens.map(({ tipo, rotulo, Icon }) => (
        <Button key={tipo} variant="outline" size="sm" disabled={disabled || !!rodando}
          onClick={() => rodar(tipo, rotulo)} className="btn-secondary"
          data-testid={`sync-${fornecedor}-${tipo}`}>
          {rodando === tipo ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Icon className="w-4 h-4 mr-1" />}
          Importar {rotulo}
        </Button>
      ))}
    </div>
  );
}

function Contadores({ dados, fornecedor }) {
  if (!dados) return null;
  return (
    <div className="grid grid-cols-3 gap-2 text-center">
      {[['Chips', dados.chips], ['Chips ativos', dados.chips_ativos], ['Planos', dados.planos]].map(([rot, v]) => (
        <div key={rot} className="bg-zinc-950/60 border border-zinc-800 rounded-md py-2" data-testid={`resumo-${fornecedor}-${rot.replace(' ', '-').toLowerCase()}`}>
          <div className="text-lg font-bold text-white">{v ?? 0}</div>
          <div className="text-[11px] text-zinc-400">{rot}</div>
        </div>
      ))}
    </div>
  );
}

function StatusLinha({ ok, textoOk, textoErro, testId }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${ok ? 'text-emerald-400' : 'text-amber-400'}`} data-testid={testId}>
      {ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
      {ok ? textoOk : textoErro}
    </div>
  );
}

function CardTa({ resumo, onReload }) {
  const cfg = resumo?.config || {};
  return (
    <div className="dashboard-card p-5 space-y-4 border-t-4 border-t-sky-600" data-testid="card-ta">
      <div>
        <h2 className="text-lg font-semibold text-white">Tá Telecom</h2>
        <p className="text-xs text-zinc-400">Antiga. Usada só para os chips que a Tip não conhece.</p>
      </div>
      <StatusLinha ok={cfg.mode === 'real'} textoOk={`Conectada · ${cfg.api_url}`} textoErro="Modo de teste (sem conexão real)" testId="status-ta" />
      <Contadores dados={resumo?.operadoras?.ta} fornecedor="ta" />
      <SyncButtons fornecedor="ta" disabled={false} onDone={onReload} />
    </div>
  );
}

function CardTip({ resumo, onReload }) {
  const tip = resumo?.config?.tip || {};
  const [token, setToken] = useState('');
  const [url, setUrl] = useState(TIP_URL_PADRAO);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);

  useEffect(() => { if (tip.api_url) setUrl(tip.api_url); }, [tip.api_url]);

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/operadora/tip/config`, { user_token: token, api_url: url }, { withCredentials: true });
      if (data.success) toast.success(`Tip conectada! ${data.planos_encontrados} plano(s) encontrados.`);
      else toast.error(`Token salvo, mas a Tip recusou: ${data.message}`);
      setToken('');
      onReload();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Erro ao salvar');
    }
    setSalvando(false);
  };

  const testar = async () => {
    setTestando(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/operadora/tip/test`, {}, { withCredentials: true });
      if (data.success) toast.success(`Conexão OK (${data.response_time_ms} ms) · ${data.planos_encontrados} plano(s)`);
      else toast.error(`Falha: ${data.message}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao testar');
    }
    setTestando(false);
  };

  return (
    <div className="dashboard-card p-5 space-y-4 border-t-4 border-t-violet-600" data-testid="card-tip">
      <div>
        <h2 className="text-lg font-semibold text-white">Tip Brasil</h2>
        <p className="text-xs text-zinc-400">Operadora principal. Ao importar chips daqui, todos os chips que a Tip conhece passam para a Tip.</p>
      </div>
      <StatusLinha ok={tip.configurada} textoOk={`Token cadastrado (final ${tip.token_final})`} textoErro="Token ainda não cadastrado" testId="status-tip" />
      <form onSubmit={salvar} className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-zinc-300 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Token da API (menu API &gt; Documentação na Tip)</Label>
          <Input type="password" autoComplete="off" value={token} onChange={e => setToken(e.target.value)}
            placeholder={tip.configurada ? 'Digite para trocar o token' : 'Cole o token aqui'}
            className="form-input font-mono" data-testid="input-tip-token" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-zinc-300">Endereço da API</Label>
          <Input value={url} onChange={e => setUrl(e.target.value)} className="form-input font-mono text-xs" data-testid="input-tip-url" />
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={salvando || token.trim().length < 6} className="btn-primary" data-testid="btn-salvar-tip">
            {salvando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PlugZap className="w-4 h-4 mr-1" />}
            Salvar e testar
          </Button>
          <Button type="button" variant="outline" disabled={!tip.configurada || testando} onClick={testar} className="btn-secondary" data-testid="btn-testar-tip">
            {testando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Testar conexão
          </Button>
        </div>
      </form>
      <Contadores dados={resumo?.operadoras?.tip} fornecedor="tip" />
      <SyncButtons fornecedor="tip" disabled={!tip.configurada} onDone={onReload} />
    </div>
  );
}

export default function Operadoras() {
  const [resumo, setResumo] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/operadora/resumo`, { withCredentials: true });
      setResumo(data);
    } catch (e) {
      toast.error('Erro ao carregar operadoras');
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div className="space-y-6" data-testid="operadoras-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Operadoras</h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          A Tip é a operadora principal: ativação, bloqueio, desbloqueio, consulta, recarga, portabilidade e consumo vão para a Tip,
          inclusive pelo robô de bloqueio. A Tá fica só para os chips que a Tip não conhece.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CardTa resumo={resumo} onReload={carregar} />
        <CardTip resumo={resumo} onReload={carregar} />
      </div>
      <ConciliacaoTip tipConfigurada={!!resumo?.config?.tip?.configurada} />
      <DivergenciasTip tipConfigurada={!!resumo?.config?.tip?.configurada} />
      <div className="text-xs text-zinc-500 max-w-2xl">
        Para vender chips da Tip: importe os planos da Tip, crie ofertas usando esses planos e vincule os chips da Tip a essas ofertas.
        O sistema não deixa misturar chip de uma operadora com plano da outra.
      </div>
    </div>
  );
}
